import crypto from 'crypto';
import { keyBy, lowerCase } from 'lodash';
import { FileKindEnum } from 'scribe-web-shared/enums/file-kind-enums';
import { WorkspaceDocument } from 'scribe-web-shared/types/document';
import { documentFilterDropdownItems, timeframeFilterDropdownItems } from 'v2/components/_workspace/insights/FilterDropdowns/constants';
import { OptionType, TimeframeEnum } from 'v2/types';

export const showCustomLogoForFile = (file: WorkspaceDocument): boolean => {
  /**
   * Determine if you should show the custom logo for a file
   * @param file - Either one of 'scribe' or 'document'
   * @returns boolean - if you should show the custom logo for the file
   * */
  return !!file?.organization_owner?.logo?.download_url;
};

export const showDefaultLogoForFile = (file: WorkspaceDocument, isEditMode: boolean): boolean => {
  /**
   * Determine if you should show the scribe logo for a file
   * @param file - Either one of 'scribe' or 'document'
   * @returns boolean - if you should show the scribe logo for the file
   * */
  return file?.organization_owner?.show_made_with_scribe && !file?.organization_owner?.logo?.download_url && !isEditMode;
};

/**
 * Shortens text to maxLength and adds elipsis if text is longer than maxLength
 * @param text
 * @param maxLength
 * @returns truncated text with elipsis if text is longer than maxLength
 */
export const truncateIfNecessary = (text: string, maxLength: number) => {
  if (text.length < maxLength) {
    return text;
  }
  return `${text.slice(0, maxLength).trim()}...`;
};

/**
 * Returns whether the current environment is on the server or not (client-side rendering)
 */
export const isServerMode = () => {
  return typeof window === 'undefined';
};

/**
 * A safe version of localStorage that works on the server
 */
export const safeLocalStorage = {
  getItem: (key: string) => {
    return isServerMode() ? null : localStorage.getItem(key);
  },
  setItem: (key: string, value: string) => {
    if (!isServerMode()) {
      localStorage.setItem(key, value);
    }
  },
  removeItem: (key: string) => {
    if (!isServerMode()) {
      localStorage.removeItem(key);
    }
  },
  clear: () => {
    if (!isServerMode()) {
      localStorage.clear();
    }
  }
};

export const safeJSONParse = <T>(jsonString: string): T | null => {
  try {
    return JSON.parse(jsonString);
  } catch (e) {
    return null;
  }
};

/**
 * Gets option from dropdown options by value
 * @param dropdownOptions
 * @param value
 * @returns OptionType
 */
export const getOptionByValue = <T extends string | number>(dropdownOptions: OptionType[], value?: T) => {
  return keyBy(dropdownOptions, 'value')[value];
};

/**
 * Gets empty state description copy based on the following parameters
 * @param fileType
 * @param timeframe
 * @param teamName
 * @returns string
 */
export const getEmptyStateDescription = (fileType: FileKindEnum, timeframe: TimeframeEnum, teamName?: string, activityType?: string) => {
  const teamNameText = ['All My Teams', 'All Teams'].includes(teamName) ? '' : teamName;
  const fileTypeText = !fileType ? 'documents' : getOptionByValue(documentFilterDropdownItems, fileType).label;

  return `There isn't any ${activityType ? `${activityType} ` : ''}activity on ${fileTypeText} ${
    teamNameText && `in ${teamNameText}`
  } in the ${lowerCase(getOptionByValue(timeframeFilterDropdownItems, timeframe).label)}.`;
};

/**
 * Returns a number parsed from a string. ie: '100%' -> 100
 * @param str
 * @returns number
 */
export const parseNumberFromString = (str: string): number => {
  try {
    if (!str) {
      return -1;
    }
    const int = +str.replace(/%$/, '');

    return Number.isNaN(int) ? -1 : int;
  } catch (e) {
    return -1;
  }
};

// Function to normalize and hash string
export function hashString(stringInput: string): string | null {
  if (!stringInput) {
    return null;
  }
  // Trim leading/trailing spaces and convert to lowercase
  const normalizedString = stringInput.trim().toLowerCase();

  // Create SHA-256 hash
  const hash = crypto.createHash('sha256');

  hash.update(normalizedString);

  // Return the hash in hexadecimal format
  return hash.digest('hex');
}

export const getProCheckoutRoute = (proType: string | null, proPeriod: string | null): string => {
  const queryParams = new URLSearchParams();

  if (proType && (proType === 'individual' || proType === 'five_user_team')) {
    queryParams.append('plan_type', proType);
  }

  if (proPeriod && (proPeriod === 'monthly' || proPeriod === 'annual')) {
    queryParams.append('plan_period', proPeriod);
  }

  return queryParams.toString() ? `/billing/checkout?${queryParams.toString()}` : '/billing/checkout';
};
